import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";
import { ColDef } from "ag-grid-community";
import images from "../../../assets/images";
import {
  getAllEmergencyResponsePlans,
  deleteEmergencyResponsePlan,
} from "../../../service/api";
import { toast } from "react-toastify";
import { useRoleManageStore } from "../../../store/store";

interface EmergencyResponsePlan {
  _id: string;
  erpName: string;
  scenarioType: string;
  scenarioTypeOther?: string;
  steps: Array<{ stepDescription: string; order: number }>;
  assignedRoleId: string;
  assignedRoleName?: string;
  responseTimeSLA: number;
  createdAt?: string;
  updatedAt?: string;
}

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const EmergencyResponsePlans: React.FC = () => {
  const navigate = useNavigate();
  const [rowData, setRowData] = useState<EmergencyResponsePlan[]>([]);
  const [loading, setLoading] = useState(false);
  const gridRef = useRef<AgGridReact>(null);
  const { rolesData, fetchAllRoles } = useRoleManageStore();

  useEffect(() => {
    fetchAllRoles();
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await getAllEmergencyResponsePlans();
      if (response.data?.resCode === 200 && response.data?.data) {
        const plans = Array.isArray(response.data.data)
          ? response.data.data
          : [];

        // Map role names to plans
        const plansWithRoleNames = plans.map((plan: EmergencyResponsePlan) => {
          const role = rolesData.find((r) => r._id === plan.assignedRoleId);
          return {
            ...plan,
            assignedRoleName: role?.roleName || "N/A",
            scenarioTypeDisplay:
              plan.scenarioType === "Other" && plan.scenarioTypeOther
                ? `${plan.scenarioType} (${plan.scenarioTypeOther})`
                : plan.scenarioType,
          };
        });

        setRowData(plansWithRoleNames);
      }
    } catch (error: any) {
      console.error("Error fetching Emergency Response Plans:", error);
      toast.error(
        error.response?.data?.resMessage ||
        "Failed to fetch Emergency Response Plans"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Refresh plans when roles are loaded to update role names and scenario type display
    if (rolesData.length > 0 && rowData.length > 0) {
      const plansWithRoleNames = rowData.map((plan) => {
        const role = rolesData.find((r) => r._id === plan.assignedRoleId);
        return {
          ...plan,
          assignedRoleName: role?.roleName || "N/A",
          scenarioTypeDisplay:
            plan.scenarioType === "Other" && plan.scenarioTypeOther
              ? `${plan.scenarioType} (${plan.scenarioTypeOther})`
              : plan.scenarioType,
        };
      });
      setRowData(plansWithRoleNames);
    }
  }, [rolesData.length]);

  const handleAddResponsePlan = () => {
    navigate("/external-emergency-preparedness/add-response-plan");
  };

  const handleView = (plan: EmergencyResponsePlan) => {
    navigate(
      `/external-emergency-preparedness/add-response-plan?id=${plan._id}&mode=view`
    );
  };

  const handleEdit = (plan: EmergencyResponsePlan) => {
    navigate(
      `/external-emergency-preparedness/add-response-plan?id=${plan._id}&mode=edit`
    );
  };

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this Emergency Response Plan?"
    );
    if (confirmed) {
      try {
        const response = await deleteEmergencyResponsePlan(id);
        if (response.data?.resCode === 200) {
          toast.success(
            response.data?.resMessage || "Emergency Response Plan deleted successfully"
          );
          fetchPlans();
        } else {
          throw new Error(
            response.data?.resMessage || "Failed to delete Emergency Response Plan"
          );
        }
      } catch (error: any) {
        console.error("Error deleting Emergency Response Plan:", error);
        toast.error(
          error.response?.data?.resMessage ||
          error.message ||
          "Failed to delete Emergency Response Plan"
        );
      }
    }
  };

  const columnDefs = useMemo<ColDef<EmergencyResponsePlan>[]>(
    () => [
      {
        field: "erpName",
        headerName: "ERP Name",
        minWidth: 200,
        flex: 1.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px", textAlign: "left" },
      },
      {
        headerName: "Scenario Type",
        minWidth: 180,
        flex: 1.2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px", textAlign: "left" },
        valueGetter: (params: any) =>
          params.data?.scenarioTypeDisplay || params.data?.scenarioType || "",
      },
      {
        field: "steps",
        headerName: "Number of Steps",
        minWidth: 120,
        flex: 0.8,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px", textAlign: "center" },
        valueGetter: (params: any) => {
          return params.data?.steps?.length || 0;
        },
      },
      {
        field: "assignedRoleName",
        headerName: "Assigned Role/Team",
        minWidth: 180,
        flex: 1.2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px", textAlign: "left" },
        valueGetter: (params: any) => {
          return params.data?.assignedRoleName || "N/A";
        },
      },
      {
        field: "responseTimeSLA",
        headerName: "Response Time SLA (Minutes)",
        minWidth: 180,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px", textAlign: "center" },
        valueFormatter: (params: any) => {
          return params.value ? `${params.value} min` : "N/A";
        },
      },
      {
        headerName: "Action",
        cellStyle: { padding: "10px 15px 0 0", textAlign: "left" },
        headerStyle: { backgroundColor: "#e9f5ff" },
        minWidth: 120,
        maxWidth: 140,
        cellRenderer: (params: any) => {
          const plan = params.data as EmergencyResponsePlan;
          return (
            <div className="flex gap-x-3 pt-2">
              <button
                onClick={() => handleView(plan)}
                className="p-1 hover:bg-gray-100 rounded"
                title="View Emergency Response Plan"
              >
                <img
                  className="iconTd w-5 h-5"
                  src={images.viewIcon}
                  alt="view"
                />
              </button>
              <button
                onClick={() => handleEdit(plan)}
                className="p-1 hover:bg-gray-100 rounded"
                title="Edit Emergency Response Plan"
              >
                <img
                  className="iconTd w-5 h-5"
                  src={images.editIcon}
                  alt="edit"
                />
              </button>
              <button
                onClick={() => handleDelete(plan._id)}
                className="p-1 hover:bg-red-100 rounded"
                title="Delete Emergency Response Plan"
              >
                <Trash2 className="text-red-500" size={20} />
              </button>
            </div>
          );
        },
        filter: false,
        sortable: false,
      },
    ],
    [rolesData]
  );

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: {
        color: "#ececec",
        bgColor: "#1f2937",
        textColor: "#fff",
      },
    };
  }, []);

  const theme = useMemo(() => gridTheme, []);

  useEffect(() => {
    // Auto-size columns on mount
    setTimeout(() => {
      gridRef.current?.api.sizeColumnsToFit();
    }, 0);
  }, [rowData]);

  return (
    <div className="px-2 py-4 mt-14 w-full h-full">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold text-gray-900">
          Emergency Response Plans
        </h1>
        <button
          className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full hover:bg-teal-600 transition-colors"
          onClick={handleAddResponsePlan}
        >
          <Plus size={20} />
          Add Response Plan
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-4">
        {rowData.length > 0 ? (
          <div className="ag-theme-quartz w-full min-h-[400px]">
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              theme={theme}
              headerHeight={65}
              rowHeight={50}
              loading={loading}
              defaultColDef={defaultColDef}
              domLayout="autoHeight"
              pagination={true}
              paginationPageSize={10}
              animateRows={true}
            />
          </div>
        ) : (
          <div className="flex flex-col justify-center items-center h-64">
            <p className="text-gray-500 text-lg mb-4">
              {loading
                ? "Loading Emergency Response Plans..."
                : "No Emergency Response Plans found. Click 'Add Response Plan' to create one."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmergencyResponsePlans;
